/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * Page SubscriberId Finances Tab component
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.12.0
 */

import { Fragment } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { useSelector } from 'react-redux';

import { ELinkTarget } from '@/main/components/common/ExternalLink/ExternalLink';
import {
    makeSelectAuthExpenses,
    makeSelectFeatureEnabled,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import { Button, ExternalLink, LinkBlock, Skeleton } from '@components/common';
import InfoBlock from '@components/common/InfoBlock/InfoBlock';
import { EColorStatus } from '@modules/app/appConstants';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { ExternalRoutePath } from '@routes/constants/externalRoutes';
import { RoutePath, SUBSCRIBER_ID } from '@routes/constants/routesConstants';
import { formatBalance, formatDate } from '@utils/formatText';
import { stringFormat, stringFormatParams, stringFormatParamsHtml } from '@utils/stringFormat';

import { useCheckCustomRole } from '../../../../../utils/useCheckCustomRole';
import WidgetCostsChart from '../../../../PageDashboard/components/Widgets/WidgetCosts/components/WidgetCostsChart';
import WidgetCostsChartEmpty from '../../../../PageDashboard/components/Widgets/WidgetCosts/components/WidgetCostsChartEmpty';
import { useSubscriberIdContext } from '../../../context';
import SubscriberIdError from '../../SubscriberIdError';
import { financeTranslationSelector } from '../selectors';
import { useGetChartDataQuery } from '../store';
import {
    SubscriberIdFinancesEmptyStyled,
    SubscriberIdFinancesPreliminaryDataContainer,
    SubscriberIdFinancesStyled,
} from './styled/SubscriberIdFinancesStyled';
import { SubscriberIdFinanceConfiguration } from './subscriberIdFinancesConstant';
import SubscriberIdFinancesSkeleton from './SubscriberIdFinancesSkeleton';

const SubscriberIdFinances = () => {
    const { subscriberId } = useSubscriberIdContext();

    const isPreliminaryDataFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.EXPENSES_PRELIMINARY_DATA),
    );

    const authExpensesData = useSelector(makeSelectAuthExpenses());

    const { data, isFetching, isSuccess, error, refetch } = useGetChartDataQuery(subscriberId);

    const tabTranslation = useSelector(financeTranslationSelector());
    const { currency } = useSelector(makeSelectTranslationCommon());

    const isCallDetailsEnabled = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardCallsDetail),
    );

    const isCustomRoleCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_CALLS_DETAILING);

    const callDetailsUrl = isCallDetailsEnabled
        ? RoutePath.callsDetailMobileInfo.replace(SUBSCRIBER_ID, subscriberId)
        : stringFormat(ExternalRoutePath.mobileDetailsId, subscriberId);

    if (error || (isSuccess && !data)) return <SubscriberIdError refetch={refetch} />;
    if (isFetching) return <Skeleton isLoading render={SubscriberIdFinancesSkeleton} />;
    if (!data) return null;

    if (!data.aggregates.find((aggregate) => aggregate.value > 0))
        return (
            <SubscriberIdFinancesEmptyStyled>
                <WidgetCostsChartEmpty currency={currency} isLegendDisable={true} isTooltipDisable={true} total={0} />
                <div className="finance-empty__title">{tabTranslation.empty}</div>
            </SubscriberIdFinancesEmptyStyled>
        );

    return (
        <SubscriberIdFinancesStyled>
            {isPreliminaryDataFeatureEnabled && (
                <SubscriberIdFinancesPreliminaryDataContainer>
                    <Typography>
                        {stringFormatParamsHtml(tabTranslation.preliminaryData, {
                            link: (
                                <ExternalLink
                                    href={authExpensesData?.urls.preliminaryData ?? '#'}
                                    target={ELinkTarget.BLANK}
                                >
                                    {tabTranslation.preliminaryDataLink}
                                </ExternalLink>
                            ),
                        })}
                    </Typography>
                </SubscriberIdFinancesPreliminaryDataContainer>
            )}

            <div className="subscriber-id-finances__title">
                <div className="subscriber-id-finances__total">
                    <span>{tabTranslation.total.title}</span>
                    <span className="subscriber-id-finances__total-value">
                        {formatBalance(data.total)}&nbsp;{currency}
                    </span>
                </div>
                <div className="subscriber-id-finances__date">
                    {stringFormatParams(tabTranslation.total.date, {
                        [ELocaleKeys.date]: formatDate(data.date),
                    })}
                </div>
            </div>
            <WidgetCostsChart
                configuration={SubscriberIdFinanceConfiguration}
                currency={currency}
                data={data.aggregates}
                total={data.total}
            >
                <Fragment>
                    {data.isDataIncomplete && (
                        <InfoBlock className="subscriber-id-finances__warning" colorStatus={EColorStatus.WARNING}>
                            {tabTranslation.dataIncomplete}
                        </InfoBlock>
                    )}
                    {isCustomRoleCheckAvailable && (
                        <LinkBlock isExternalUrl={!isCallDetailsEnabled} url={callDetailsUrl}>
                            <Button className="subscriber-id-finances__btn" kind="medium">
                                {tabTranslation.btnCallDetails}
                            </Button>
                        </LinkBlock>)}
                </Fragment>
            </WidgetCostsChart>
        </SubscriberIdFinancesStyled>
    );
};

export default SubscriberIdFinances;
